import { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import {
  Stack,
  Text,
  Button,
  Alert,
  Group,
  SegmentedControl,
  TextInput,
  Paper,
  List,
  Divider,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconMapPin,
  IconSearch,
  IconCurrentLocation,
  IconCar,
  IconTruck
} from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { usePostHog } from '@posthog/react';
import { AnalyticsEvent } from '../../utils/analytics';
import { initiateKrogerConnection } from '../../utils/krogerAuth';
import { KrogerLocation } from '../../types/kroger';
import { KrogerInfo, ProductPreferences } from '../../types/familyProfile';
import { isValidZipCode, searchKrogerLocations, searchKrogerLocationsByCoords } from '../../utils/krogerLocations';
import { ProductPreference, ProductPreferenceRef } from './ProductPreference';

type GroceryPreferenceProps = {
  value: {
    krogerConnected: boolean;
    krogerInfo?: KrogerInfo;
    productPreferences?: ProductPreferences;
  };
  onChange: (value: {
    krogerConnected: boolean;
    krogerInfo?: KrogerInfo;
    productPreferences?: ProductPreferences;
  }) => void;
};

// Forwarded ref exposes the inner ProductPreference flushInputs so ProfilePage
// can keep its existing "flush all text inputs before save" flow.
export type GroceryPreferenceRef = ProductPreferenceRef;

export const GroceryPreference = forwardRef<GroceryPreferenceRef, GroceryPreferenceProps>(({ value, onChange }, ref) => {
  const productPreferenceRef = useRef<ProductPreferenceRef>(null);
  useImperativeHandle(ref, () => ({
    flushInputs: () => productPreferenceRef.current?.flushInputs(),
  }));
  const { user } = useAuth();
  const posthog = usePostHog();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Location picker state
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isUsingLocation, setIsUsingLocation] = useState(false);
  const [locations, setLocations] = useState<KrogerLocation[]>([]);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState<KrogerLocation | null>(null);
  const [fulfillmentPreference, setFulfillmentPreference] = useState<'pickup' | 'delivery'>(value.krogerInfo?.fulfillmentPreference || 'pickup');

  // Check for OAuth callback parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const krogerStatus = urlParams.get('kroger');
    const errorParam = urlParams.get('error');

    if (krogerStatus === 'connected' && !value.krogerConnected) {
      posthog?.capture(AnalyticsEvent.KrogerConnected, { source: 'profile' });
      onChange({ ...value, krogerConnected: true });
    }

    // Clean up URL parameters
    if (krogerStatus || errorParam) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        kroger_auth_failed: 'Kroger authorization was denied or failed.',
        missing_params: 'Missing required parameters from Kroger.',
        invalid_state: 'Invalid security token.',
        token_exchange_failed: 'Failed to exchange authorization code.',
      };
      setError(errorMessages[errorParam] || 'An unknown error occurred.');
    }
  }, [onChange, value, posthog]);

  // Set selected location if user location is present
  useEffect(() => {
    if (value.krogerInfo?.location && !selectedLocationDetails) {
      setSelectedLocationDetails(value.krogerInfo.location);
    }
  }, [value.krogerInfo, selectedLocationDetails]);

  const handleConnectKroger = async () => {
    if (!user) {
      setError('You must be signed in to connect Kroger.');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      await initiateKrogerConnection(user);
    } catch (error) {
      console.error('Error initiating Kroger OAuth:', error);
      setError('Failed to connect to Kroger. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsConnecting(true);
      
      const token = await user?.getIdToken();
      if (!token) {
        throw new Error('No authentication token');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/kroger/disconnect`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect from Kroger');
      }

      onChange({ 
        krogerConnected: false,
        krogerInfo: undefined,
      });
      setShowLocationPicker(false);
      setError(null);
    } catch (error) {
      console.error('Error disconnecting from Kroger:', error);
      setError('Failed to disconnect from Kroger. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const searchLocations = async (query: string) => {
    if (!query.trim()) return;
    if (!isValidZipCode(query)) {
      setError('Please enter a valid 5-digit ZIP code.');
      return;
    }

    setIsSearching(true);
    setError(null);
    setLocations([]); // Clear previous results
    try {
      const results = await searchKrogerLocations(query);
      setLocations(results);
      if (results.length === 0) {
        setError('No Kroger stores found in this area. Try a different ZIP code.');
      }
    } catch (error) {
      console.error('Error searching locations:', error);
      setError('Failed to search locations. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationSearch = async () => {
    if (!navigator.geolocation) {
      setError('Location services are not available in your browser');
      return;
    }

    setIsUsingLocation(true);
    setError(null);
    setLocations([]); // Clear previous results

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const results = await searchKrogerLocationsByCoords(latitude, longitude);
          setLocations(results);
          if (results.length === 0) {
            setError('No Kroger stores found in your area.');
          }
        } catch (error) {
          console.error('Error searching locations by coords:', error);
          setError('Failed to find stores near your location. Please try searching manually.');
        } finally {
          setIsUsingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setError('Unable to access your location. Please check your browser permissions and try again.');
        setIsUsingLocation(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handleLocationSelect = (location: KrogerLocation) => {
    setSelectedLocationDetails(location);
    onChange({
      ...value,
      krogerInfo: {
        ...(value.krogerInfo || {}),
        location,
        fulfillmentPreference,
      }
    });
    setShowLocationPicker(false);
  };

  const handleFulfillmentChange = (newFulfillment: 'pickup' | 'delivery') => {
    setFulfillmentPreference(newFulfillment);
    onChange({
      ...value,
      krogerInfo: {
        ...(value.krogerInfo || {}),
        fulfillmentPreference: newFulfillment,
      }
    });
  };


  if (!value.krogerConnected) {
    return (
      <Stack gap="sm">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="lg">
            {error}
          </Alert>
        )}
        
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" radius="lg">
          <Stack>
            <Text size="sm">
              <strong>Benefits of connecting Kroger</strong>
            </Text>
            <List
              spacing="sm"
              size="sm"
            >
              <List.Item>Integrated shopping list</List.Item>
              <List.Item>AI personal grocery shopper</List.Item>
              <List.Item>1-click meal plan add-to-cart</List.Item>
            </List> 
          </Stack>
        </Alert>
        
        <Button
          onClick={handleConnectKroger}
          loading={isConnecting}
          color="blue"
          size="sm"
          w={{ base: '100%', sm: '220px' }}
          style={{ alignSelf: 'center' }}
        >
          {isConnecting ? 'Connecting...' : 'Connect Kroger account'}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Alert icon={<IconCheck size={16} />} color="green" variant="light" radius="lg">
        Your Kroger account is connected! You can add ingredients directly to your cart.
      </Alert>
      
      {/* Store Location & Fulfillment */}
      <Stack gap="sm">
        <Text fw={500}>Store location & fulfillment</Text>
        
        {value.krogerInfo?.location ? (
          <Stack gap="sm">
            {/* Current Store Display */}
            <Paper p="md" bg="gray.0" radius="lg" withBorder>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconMapPin size={18} color="var(--mantine-color-orange-6)" />
                  <Stack gap="xs" style={{ flex: 1 }}>
                    <Text fw={500} size="sm">
                      {selectedLocationDetails ? selectedLocationDetails.name : 'Kroger Store'}
                    </Text>
                    {selectedLocationDetails ? (
                      <Text size="xs" c="dimmed">
                        {selectedLocationDetails.address.addressLine1}, {selectedLocationDetails.address.city}, {selectedLocationDetails.address.state} {selectedLocationDetails.address.zipCode}
                      </Text>
                    ) : (
                      <Text size="xs" c="dimmed">
                        Location ID: {value.krogerInfo.location.locationId}
                      </Text>
                    )}
                  </Stack>
                  <Button size="xs" variant="subtle" onClick={() => setShowLocationPicker(true)}>
                    Change store
                  </Button>
                </Group>
              </Stack>
            </Paper>
            
            {/* Fulfillment Preference */}
            <Stack gap="xs">
              <Text fw={500}>Fulfillment method</Text>
              <SegmentedControl
                value={value.krogerInfo?.fulfillmentPreference || 'pickup'}
                onChange={(val) => handleFulfillmentChange(val as 'pickup' | 'delivery')}
                data={[
                  { 
                    label: (
                      <Group gap="xs" justify="center">
                        <IconCar size={16} />
                        <Text size="sm">Pickup</Text>
                      </Group>
                    ), 
                    value: 'pickup' 
                  },
                  { 
                    label: (
                      <Group gap="xs" justify="center">
                        <IconTruck size={16} />
                        <Text size="sm">Delivery</Text>
                      </Group>
                    ), 
                    value: 'delivery' 
                  }
                ]}
                size="md"
                color="orange"
                fullWidth
                styles={{
                  root: {
                    background: 'transparent'
                  },
                  control: {
                    padding: '8px 12px'
                  }
                }}
              />
            </Stack>
          </Stack>
        ) : (
          <Paper p="md" bg="gray.1" radius="lg" style={{ textAlign: 'center' }}>
            <Stack gap="sm" align="center">
              <IconMapPin size={32} color="var(--mantine-color-orange-4)" />
              <Text size="sm" c="dimmed">No store selected</Text>
              <Button 
                variant="filled" 
                size="sm" 
                leftSection={<IconMapPin size={16} color="white" />}
                onClick={() => setShowLocationPicker(true)}
              >
                Select store location
              </Button>
            </Stack>
          </Paper>
        )}
        
        {showLocationPicker && (
          <Paper p="md" bg="gray.0" radius="lg" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={600} size="md">Change store location</Text>
                <Button size="xs" variant="subtle" onClick={() => setShowLocationPicker(false)}>
                  Cancel
                </Button>
              </Group>
              
              <Group>
                <TextInput
                  placeholder="Enter ZIP code"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchLocations(searchTerm)}
                  style={{ flex: 1 }}
                  size="md"
                  styles={{
                    input: {
                      fontSize: '16px',
                      padding: '12px 16px'
                    }
                  }}
                />
                <Button 
                  onClick={() => searchLocations(searchTerm)} 
                  loading={isSearching}
                  size="sm"
                >
                  <IconSearch size={14} />
                </Button>
                <Button
                  onClick={handleLocationSearch}
                  loading={isUsingLocation}
                  variant="light"
                  size="sm"
                  leftSection={<IconCurrentLocation size={14} />}
                >
                  {isUsingLocation ? 'Finding stores...' : 'Use my location'}
                </Button>
              </Group>
              
              {error && (
                <Alert icon={<IconAlertCircle size={14} />} color="red" variant="light" radius="lg">
                  {error}
                </Alert>
              )}
              
              {locations.length > 0 && (
                <Stack gap="xs" mah={200} style={{ overflowY: 'auto' }}>
                  <Text size="sm" fw={500} c="dimmed">Select a store:</Text>
                  {locations.map((location) => (
                    <Paper
                      key={location.locationId}
                      p="md"
                      radius="lg"
                      style={{ cursor: 'pointer' }}
                      bg="white"
                      withBorder
                      onClick={() => handleLocationSelect(location)}
                      className="hover:bg-gray-50"
                    >
                      <Group gap="sm">
                        <IconMapPin size={16} color="var(--mantine-color-orange-6)" />
                        <Stack gap="xs" style={{ flex: 1 }}>
                          <Text size="sm" fw={500}>{location.name}</Text>
                          <Text size="xs" c="dimmed">
                            {location.address.addressLine1}, {location.address.city}, {location.address.state} {location.address.zipCode}
                          </Text>
                        </Stack>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Stack>
          </Paper>
        )}
      </Stack>

      
      <Divider />

      <ProductPreference
        ref={productPreferenceRef}
        value={value.productPreferences || {}}
        onChange={(next) => onChange({ ...value, productPreferences: next })}
      />

      <Button
        variant="subtle"
        color="red"
        size="sm"
        onClick={handleDisconnect}
        loading={isConnecting}
        style={{ alignSelf: 'center' }}
      >
        Disconnect Kroger
      </Button>
    </Stack>
  );
});
